import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart,
  ClipboardText,
  Handshake,
  SignOut,
  House,
  MagnifyingGlass,
  User,
  MapPin,
  CheckCircle,
  X,
  Calendar,
  Plus,
  Spinner
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMatching = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedUser = searchParams.get('user');
  
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    gender: '',
    location: '',
    status: 'submitted'
  });
  
  // Selection state
  const [selectedUserA, setSelectedUserA] = useState(null);
  const [selectedUserB, setSelectedUserB] = useState(null);
  
  // Match dialog
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [matchNotes, setMatchNotes] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Schedule dialog
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [timeSlots, setTimeSlots] = useState(['', '', '']);
  const [scheduling, setScheduling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, matchesRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/matches`)
      ]);
      setUsers(usersRes.data);
      setMatches(matchesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (preselectedUser && users.length > 0) {
      const found = users.find((u) => u.id === preselectedUser);
      if (found) setSelectedUserA(found);
    }
  }, [preselectedUser, users]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredUsers = users.filter(u => {
    if (filters.gender && u.gender !== filters.gender) return false;
    if (filters.location && !u.location?.toLowerCase().includes(filters.location.toLowerCase())) return false;
    if (filters.status && u.application_status !== filters.status) return false;
    return true;
  });

  const handleSelectUser = (user) => {
    if (!selectedUserA) {
      setSelectedUserA(user);
    } else if (!selectedUserB && user.id !== selectedUserA.id) {
      setSelectedUserB(user);
      setShowMatchDialog(true);
    }
  };

  const clearSelection = () => {
    setSelectedUserA(null);
    setSelectedUserB(null);
    setMatchNotes('');
  };

  const createMatch = async () => {
    if (!selectedUserA || !selectedUserB) return;
    
    setCreating(true);
    try {
      await axios.post(`${API}/admin/match/create`, {
        user_a_id: selectedUserA.id,
        user_b_id: selectedUserB.id,
        admin_notes: matchNotes
      });
      toast.success('Match created successfully!');
      setShowMatchDialog(false);
      clearSelection();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create match';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const openScheduleDialog = (match) => {
    setSelectedMatch(match);
    setTimeSlots(['', '', '']);
    setShowScheduleDialog(true);
  };

  const scheduleMatch = async () => {
    const validSlots = timeSlots.filter(s => s.trim() !== '');
    if (validSlots.length === 0) {
      toast.error('Please add at least one time slot');
      return;
    }

    setScheduling(true);
    try {
      await axios.post(`${API}/admin/match/${selectedMatch.id}/schedule`, {
        match_id: selectedMatch.id,
        slots: validSlots
      });
      toast.success('Time slots sent to users!');
      setShowScheduleDialog(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to schedule';
      toast.error(message);
    } finally {
      setScheduling(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-amber-500/20 text-amber-400',
      'scheduling': 'bg-blue-500/20 text-blue-400',
      'scheduled': 'bg-emerald-500/20 text-emerald-400',
      'completed': 'bg-stone-500/20 text-stone-400'
    };
    return colors[status] || 'bg-stone-500/20 text-stone-400';
  };

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Heart weight="fill" className="w-6 h-6 text-primary" />
              <span className="font-serif text-xl text-white">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-stone-400 text-sm">Welcome, {user?.name}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-stone-400 hover:text-white"
              >
                <SignOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-stone-800/50 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6">
            <Link 
              to="/admin" 
              className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium"
            >
              <House className="w-4 h-4 inline mr-2" />
              Overview
            </Link>
            <Link 
              to="/admin/applications" 
              className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium"
            >
              <ClipboardText className="w-4 h-4 inline mr-2" />
              Applications
            </Link>
            <Link 
              to="/admin/matching" 
              className="py-4 text-white border-b-2 border-primary text-sm font-medium"
            >
              <Handshake className="w-4 h-4 inline mr-2" />
              Matching
            </Link>
            <Link to="/admin/team" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              Admin Team
            </Link>
            <Link to="/admin/account" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              My Account
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Users Panel */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-white" data-testid="matching-title">
                Select Users to Match
              </h2>
              {(selectedUserA || selectedUserB) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearSelection}
                  className="text-stone-400"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Selection
                </Button>
              )}
            </div>

            {/* Selection Status */}
            {selectedUserA && (
              <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 mb-6">
                <p className="text-primary text-sm mb-2">
                  {selectedUserB 
                    ? 'Both users selected! Review the match details.'
                    : 'First user selected. Now select the second user to match with.'}
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle weight="fill" className="w-4 h-4 text-primary" />
                    <span className="text-white">{selectedUserA.name}</span>
                  </div>
                  {selectedUserB && (
                    <>
                      <Heart weight="fill" className="w-4 h-4 text-primary" />
                      <div className="flex items-center gap-2">
                        <CheckCircle weight="fill" className="w-4 h-4 text-primary" />
                        <span className="text-white">{selectedUserB.name}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <Select 
                value={filters.gender} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, gender: value }))}
              >
                <SelectTrigger className="w-32 bg-stone-800 border-stone-700 text-white">
                  <SelectValue placeholder="Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="non-binary">Non-binary</SelectItem>
                </SelectContent>
              </Select>
              
              <Input
                placeholder="Filter by location"
                value={filters.location}
                onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                className="w-48 bg-stone-800 border-stone-700 text-white"
              />
            </div>

            {/* Users Grid */}
            {loading ? (
              <div className="text-stone-400 text-center py-12">Loading users...</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredUsers.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleSelectUser(u)}
                    disabled={selectedUserA?.id === u.id}
                    className={`text-left bg-stone-800 rounded-sm p-4 transition-all ${
                      selectedUserA?.id === u.id 
                        ? 'ring-2 ring-primary opacity-50 cursor-not-allowed' 
                        : selectedUserB?.id === u.id
                        ? 'ring-2 ring-primary'
                        : 'hover:bg-stone-700'
                    }`}
                    data-testid={`user-card-${u.id}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-stone-700 flex items-center justify-center">
                        <User weight="light" className="w-5 h-5 text-stone-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{u.name}</h3>
                        <p className="text-stone-400 text-sm">{u.age} • {u.gender}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-stone-400 text-sm">
                      <MapPin className="w-4 h-4" />
                      {u.location}
                    </div>
                    {u.application?.relationship_goal && (
                      <Badge className="mt-2 bg-stone-700 text-stone-300 text-xs">
                        {u.application.relationship_goal}
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Matches Panel */}
          <div>
            <h2 className="font-serif text-2xl text-white mb-6">Recent Matches</h2>
            
            <div className="space-y-4">
              {matches.slice(0, 10).map((match) => (
                <div 
                  key={match.id} 
                  className="bg-stone-800 rounded-sm p-4"
                  data-testid={`match-item-${match.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm">{match.user_a?.name}</span>
                      <Heart weight="fill" className="w-4 h-4 text-primary" />
                      <span className="text-white text-sm">{match.user_b?.name}</span>
                    </div>
                    <Badge className={getStatusColor(match.status)}>
                      {match.status}
                    </Badge>
                  </div>
                  
                  {match.status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => openScheduleDialog(match)}
                      className="w-full btn-primary"
                      data-testid={`schedule-match-${match.id}`}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Call
                    </Button>
                  )}
                  
                  {match.scheduled_time && (
                    <p className="text-stone-400 text-xs mt-2">
                      Scheduled: {new Date(match.scheduled_time).toLocaleString()}
                    </p>
                  )}
                </div>
              ))}

              {matches.length === 0 && (
                <div className="text-stone-400 text-center py-8">
                  No matches yet
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Create Match Dialog */}
      <Dialog open={showMatchDialog} onOpenChange={setShowMatchDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Create Match</DialogTitle>
            <DialogDescription className="text-stone-400">
              Confirm the match between these two users
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-stone-700 flex items-center justify-center mx-auto mb-2">
                  <User weight="light" className="w-8 h-8 text-stone-400" />
                </div>
                <p className="text-white font-medium">{selectedUserA?.name}</p>
                <p className="text-stone-400 text-sm">{selectedUserA?.location}</p>
              </div>
              <Heart weight="fill" className="w-8 h-8 text-primary" />
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-stone-700 flex items-center justify-center mx-auto mb-2">
                  <User weight="light" className="w-8 h-8 text-stone-400" />
                </div>
                <p className="text-white font-medium">{selectedUserB?.name}</p>
                <p className="text-stone-400 text-sm">{selectedUserB?.location}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-stone-300">Notes (Optional)</Label>
              <Textarea
                placeholder="Why did you think these two would be a good match?"
                value={matchNotes}
                onChange={(e) => setMatchNotes(e.target.value)}
                className="bg-stone-700 border-stone-600 text-white"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => setShowMatchDialog(false)}
              className="text-stone-400"
            >
              Cancel
            </Button>
            <Button 
              onClick={createMatch} 
              disabled={creating}
              className="btn-primary"
              data-testid="confirm-match-btn"
            >
              {creating ? <Spinner className="w-4 h-4 animate-spin" /> : 'Create Match'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Schedule Video Call</DialogTitle>
            <DialogDescription className="text-stone-400">
              Propose time slots for {selectedMatch?.user_a?.name} and {selectedMatch?.user_b?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {timeSlots.map((slot, index) => (
              <div key={index} className="space-y-2">
                <Label className="text-stone-300">Time Slot {index + 1}</Label>
                <Input
                  type="datetime-local"
                  value={slot}
                  onChange={(e) => {
                    const newSlots = [...timeSlots];
                    newSlots[index] = e.target.value;
                    setTimeSlots(newSlots);
                  }}
                  className="bg-stone-700 border-stone-600 text-white"
                  data-testid={`time-slot-input-${index}`}
                />
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setTimeSlots([...timeSlots, ''])}
              className="text-stone-400"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Slot
            </Button>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => setShowScheduleDialog(false)}
              className="text-stone-400"
            >
              Cancel
            </Button>
            <Button 
              onClick={scheduleMatch} 
              disabled={scheduling}
              className="btn-primary"
              data-testid="send-time-slots-btn"
            >
              {scheduling ? <Spinner className="w-4 h-4 animate-spin" /> : 'Send to Users'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMatching;
